/**
 * Chunky inked lettering baked to small canvases (Ghost Circuit's popup lettering):
 * Fredoka 700, a hard ink "lip" under the word, a round-joined ink stroke, and a lit fill
 * (white on top, the colour from about the middle down, hard stops like the CSS `.inked`).
 *
 * One baker and one cache for every inked word in the game, in presets:
 *   - "popup" (default): score popups, callouts, popText — heavy ink, two-tone fill, bakes
 *     capped at 112 px;
 *   - "countdown": the Fall 3-2-1 digits (render/toon/fall/fall-lettering.js) — thinner ink so
 *     the counters of a 2 or 3 stay open, a banded fill, bakes up to 288 px (the digit is about
 *     0.6 × the flask height, × 2 on a retina canvas).
 *
 * Each word is painted once per (text, colour, preset, size bucket) and then only scaled, so a
 * popup costs one sprite (Pixi) or one drawImage (Canvas 2D). A canvas is never repainted: when
 * the font arrives after a provisional bake, the word gets a new canvas (Pixi memoises textures
 * per canvas object, see render/pixi/sprite-pool.js). Every canvas that leaves the cache (evicted,
 * or replaced after the font arrived) has its GPU texture released, since Pixi never collects
 * canvas textures on its own.
 */
import { INK, mixHex } from "../toon/palette.js";
import { releaseCanvasTexture } from "../toon/jar/texture-hints.js";

var FONT_FAMILY = "Fredoka, 'Baloo 2', system-ui, sans-serif";
var MAX_ENTRIES = 96;

/**
 * Bake recipes. inkW(px): stroke width; lip(px): drop under the word; pad(ink): margin;
 * heightEm / baselineEm: canvas height and text middle in ems; fill: the lit fill bands.
 */
var PRESETS = {
  popup: {
    bucket: 8,
    maxBake: 112,
    fontProbe: "700 24px Fredoka",
    inkW: function (px) { return Math.max(2, px * 0.2); },
    lip: function (px) { return Math.max(1, px * 0.09); },
    pad: function (inkW) { return Math.ceil(inkW + 2); },
    heightEm: 1.18,
    baselineEm: 0.6,
    fillTop: 0.5,
    fillBottom: 0.45,
    fill: function (color) {
      return [[0, "#ffffff"], [0.4, "#ffffff"], [0.4, mixHex(color, "#ffffff", 0.35)], [0.62, color], [1, color]];
    },
  },
  // Ink reaching 0.045 em past the glyph (the stroke is centred on the edge) and a 0.05 em drop:
  // any heavier and the narrow counters of a 2 or 3 fill in and the digit turns to a blob.
  countdown: {
    bucket: 16,
    maxBake: 288,
    fontProbe: "700 48px Fredoka",
    inkW: function (px) { return Math.max(2, px * 0.045) * 2; },
    lip: function (px) { return Math.max(2, px * 0.05); },
    pad: function (inkW) { return Math.ceil(inkW / 2 + 3); },
    heightEm: 1.12,
    baselineEm: 0.58,
    fillTop: 0.5,
    fillBottom: 0.42,
    // White cap, a hard step into a lighter band, then the colour; a shade band at the foot.
    fill: function (color) {
      var light = mixHex(color, "#ffffff", 0.45);
      var shade = mixHex(color, INK, 0.22);
      return [
        [0, "#ffffff"], [0.18, "#ffffff"], [0.18, light], [0.38, light],
        [0.38, color], [0.8, color], [0.8, shade], [1, shade],
      ];
    },
  },
};

var cache = new Map();
var disposer = releaseCanvasTexture;

/**
 * Who frees a canvas that leaves the cache (default: its Pixi texture via texture-hints, a no-op
 * before Pixi loads or on Canvas 2D). Passing null restores the default.
 */
export function setInkTextDisposer(fn) {
  disposer = typeof fn === "function" ? fn : releaseCanvasTexture;
}

function dispose(entry) {
  if (!entry || !entry.canvas) return;
  try {
    disposer(entry.canvas);
  } catch (_e) { /* a failed release only costs memory */ }
}

function fontReady(probe) {
  try {
    if (typeof document === "undefined" || !document.fonts || !document.fonts.check) return true;
    if (document.fonts.check(probe)) return true;
    document.fonts.load(probe).catch(function () {});
    return false;
  } catch (_e) {
    return true;
  }
}

function canBake() {
  if (typeof document === "undefined" || !document.createElement) return false;
  if (typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent || "")) return false;
  return true;
}

/** Bake size (px) for a word shown at `size` logical px on a `dpr` screen, in the preset's buckets. */
export function inkTextBakePx(size, dpr, presetName) {
  var P = PRESETS[presetName] || PRESETS.popup;
  var d = dpr > 0 ? dpr : 1;
  return Math.min(P.maxBake, Math.max(P.bucket * 2, Math.round((size * Math.min(2, d)) / P.bucket) * P.bucket));
}

/**
 * The baked word. `size` is the logical font size (px) it will be shown at; the bake is done
 * at `size × dpr` rounded to a bucket, and `w` / `h` are the logical draw size at `size`.
 * @param {string} text
 * @param {string} color lit fill colour (hex)
 * @param {number} size logical font px
 * @param {{ dpr?: number, ink?: string, lip?: boolean, preset?: "popup"|"countdown" }} [opts]
 * @returns {{ canvas: HTMLCanvasElement, w: number, h: number } | null}
 */
export function getInkTextSprite(text, color, size, opts) {
  if (!canBake() || !(size > 0)) return null;
  var o = opts || {};
  var presetName = PRESETS[o.preset] ? o.preset : "popup";
  var P = PRESETS[presetName];
  var dpr = o.dpr > 0 ? o.dpr : (typeof window !== "undefined" && window.devicePixelRatio) || 1;
  var bakePx = inkTextBakePx(size, dpr, presetName);
  var key = text + "|" + color + "|" + bakePx + "|" + presetName + "|" + (o.ink || "") + (o.lip === false ? "|nolip" : "");
  var entry = cache.get(key);
  if (entry && (!entry.provisional || !fontReady(P.fontProbe))) {
    // Refresh recency.
    cache.delete(key);
    cache.set(key, entry);
    return scaled(entry, size);
  }
  var ready = fontReady(P.fontProbe);
  var baked = bakeWord(String(text), color, bakePx, P, o);
  if (!baked) return null;
  baked.provisional = !ready;
  // The provisional (fallback-font) canvas is replaced: free its texture.
  if (entry) dispose(entry);
  cache.delete(key);
  cache.set(key, baked);
  while (cache.size > MAX_ENTRIES) {
    var oldest = cache.keys().next().value;
    var old = cache.get(oldest);
    cache.delete(oldest);
    dispose(old);
  }
  return scaled(baked, size);
}

function scaled(entry, size) {
  var k = size / entry.fontPx;
  return { canvas: entry.canvas, w: entry.canvas.width * k, h: entry.canvas.height * k };
}

function bakeWord(text, color, px, P, o) {
  var cv;
  try {
    cv = document.createElement("canvas");
  } catch (_e) {
    return null;
  }
  var c = cv.getContext && cv.getContext("2d");
  if (!c) return null;
  var font = "700 " + px + "px " + FONT_FAMILY;
  c.font = font;
  var inkW = P.inkW(px);
  var lip = o.lip === false ? 0 : P.lip(px);
  var tw = Math.ceil(c.measureText(text).width);
  var pad = P.pad(inkW);
  cv.width = tw + pad * 2;
  cv.height = Math.ceil(px * P.heightEm + pad * 2 + lip);
  c = cv.getContext("2d");
  c.font = font;
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.lineJoin = "round";
  c.miterLimit = 2;
  var x = cv.width / 2;
  var y = pad + px * P.baselineEm;
  var inkColor = o.ink || INK;
  // Hard ink lip under the word (the buttons' 0 4px 0 ink drop), then the outline.
  c.strokeStyle = inkColor;
  c.fillStyle = inkColor;
  c.lineWidth = inkW;
  if (lip > 0) {
    c.strokeText(text, x, y + lip);
    c.fillText(text, x, y + lip);
  }
  c.strokeText(text, x, y);
  // Lit fill: hard-stop bands from a white top into the colour.
  var g = c.createLinearGradient(0, y - px * P.fillTop, 0, y + px * P.fillBottom);
  var stops = P.fill(color);
  for (var i = 0; i < stops.length; i++) g.addColorStop(stops[i][0], stops[i][1]);
  c.fillStyle = g;
  c.fillText(text, x, y);
  return { canvas: cv, fontPx: px };
}

/** Test hook: entries currently cached. */
export function __inkTextCacheSize() {
  return cache.size;
}

/** Test hook. */
export function __clearInkTextCache() {
  cache.forEach(dispose);
  cache.clear();
}
